// src/App.tsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/Toast';
import Layout from './components/Layout';
import { LoadingScreen } from './components/ui';

const Dashboard       = lazy(() => import('./pages/Dashboard'));
const Login           = lazy(() => import('./pages/Login'));
const Register        = lazy(() => import('./pages/Register'));
const ForgotPassword  = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword   = lazy(() => import('./pages/ResetPassword'));
const LiveScoring     = lazy(() => import('./pages/LiveScoring'));
const Marketplace     = lazy(() => import('./pages/Marketplace'));
const PlayerProfile   = lazy(() => import('./pages/PlayerProfile'));
const MyProfile       = lazy(() => import('./pages/MyProfile'));
const ClubPage        = lazy(() => import('./pages/ClubPage'));
const Tournaments     = lazy(() => import('./pages/Tournaments'));
const TournamentDetail= lazy(() => import('./pages/TournamentDetail'));
const TournamentBoard = lazy(() => import('./pages/TournamentBoard'));
const PublicBoard     = lazy(() => import('./pages/PublicBoard'));
const MatchDetail     = lazy(() => import('./pages/MatchDetail'));
const NotFound        = lazy(() => import('./pages/NotFound'));
const UmpiresPage     = lazy(() => import('./pages/UmpiresPage'));
const VenuesPage      = lazy(() => import('./pages/VenuesPage'));
const TransfersPage   = lazy(() => import('./pages/TransfersPage'));
const DisputesPage    = lazy(() => import('./pages/DisputesPage'));
const AuctionPage     = lazy(() => import('./pages/AuctionPage'));
const DLSPage         = lazy(() => import('./pages/DLSPage'));
const FeedPage        = lazy(() => import('./pages/FeedPage'));
const JuniorPage      = lazy(() => import('./pages/JuniorPage'));
const PaymentsPage    = lazy(() => import('./pages/PaymentsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ScoringJoin       = lazy(() => import('./pages/ScoringJoin'));
const WorldCricket       = lazy(() => import('./pages/WorldCricket'));
const WorldCricketDetail = lazy(() => import('./pages/WorldCricketDetail'));
const JoinClub           = lazy(() => import('./pages/JoinClub'));
const LookingFor         = lazy(() => import('./pages/LookingFor'));

const LISTINGS_ENABLED = process.env.REACT_APP_ENABLE_LISTINGS === 'true';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function HomeRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <PublicLanding />;
}

const PublicLanding = lazy(() => import('./pages/PublicLanding'));

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <ToastProvider>
      <AuthProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/login"              element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register"           element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password"    element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            {/* /reset-password intentionally NOT in PublicRoute: the recovery
                email link arrives with a Supabase PASSWORD_RECOVERY session,
                so PublicRoute would bounce the user away before they can set
                a new password. The page itself gates rendering by event. */}
            <Route path="/reset-password"     element={<ResetPassword />} />
            <Route path="/scoreboard"         element={<PublicBoard />} />
            <Route path="/scoreboard/:matchId" element={<MatchDetail />} />
            <Route path="/public"             element={<PublicLanding />} />
            <Route path="/live"               element={<PublicBoard />} />
            <Route path="/public/feed"        element={<PublicLanding />} />
            <Route path="/scoring/join"       element={<ScoringJoin />} />
            <Route path="/join/:clubId"       element={<JoinClub />} />
            <Route path="/home"               element={<HomeRoute />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index                        element={<Dashboard />} />
              <Route path="scoring"               element={<LiveScoring />} />
              <Route path="scoring/:matchId"      element={<LiveScoring />} />
              <Route path="marketplace"           element={<Marketplace />} />
              {LISTINGS_ENABLED && (
                <Route path="looking-for"         element={<LookingFor />} />
              )}
              <Route path="players/:id"           element={<PlayerProfile />} />
              <Route path="profile"               element={<MyProfile />} />
              <Route path="clubs/:clubId"         element={<ClubPage />} />
              <Route path="tournaments"           element={<Tournaments />} />
              <Route path="tournaments/:id"       element={<TournamentDetail />} />
              <Route path="tournaments-board/:id" element={<TournamentBoard />} />
              <Route path="matches/:id"           element={<MatchDetail />} />
              <Route path="umpires"               element={<UmpiresPage />} />
              <Route path="venues"                element={<VenuesPage />} />
              <Route path="transfers"             element={<TransfersPage />} />
              <Route path="disputes"              element={<DisputesPage />} />
              <Route path="auction"               element={<AuctionPage />} />
              <Route path="auction/:id"           element={<AuctionPage />} />
              <Route path="dls"                   element={<DLSPage />} />
              <Route path="feed"                  element={<FeedPage />} />
              <Route path="junior"                element={<JuniorPage />} />
              <Route path="payments"              element={<PaymentsPage />} />
              <Route path="notifications"         element={<NotificationsPage />} />
              <Route path="world"                 element={<WorldCricket />} />
              <Route path="world/:id"             element={<WorldCricketDetail />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AuthProvider>
      </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
